import type {
  MeterBarChartThresholdIndicatorPropsInternal,
  ThresholdIntervalRange,
} from '../types/meter-bar-chart-internal.js';

/**
 * Builds the colored interval ranges rendered below the meter bar. Each
 * threshold owns the range from its value up to the next threshold (the last
 * extends to `max`); the region below the first threshold uses `fallbackColor`.
 * @param thresholds - Configured thresholds (sorted or unsorted).
 * @param min - Scale minimum.
 * @param max - Scale maximum.
 * @param fallbackColor - Color for the uncovered region and for thresholds with no color.
 */
export const buildThresholdIntervals = (
  thresholds: MeterBarChartThresholdIndicatorPropsInternal[],
  min: number,
  max: number,
  fallbackColor: string,
): ThresholdIntervalRange[] => {
  if (thresholds.length === 0) {
    return [];
  }

  const sorted = [...thresholds].sort((a, b) => a.value - b.value);

  const intervals: ThresholdIntervalRange[] = [];

  const firstStart = clamp(sorted[0].value, min, max);
  if (firstStart > min) {
    intervals.push({ start: min, end: firstStart, color: fallbackColor });
  }

  for (let index = 0; index < sorted.length; index++) {
    const start = clamp(sorted[index].value, min, max);
    const end = clamp(sorted[index + 1]?.value ?? max, min, max);

    if (end <= start) {
      continue;
    }

    intervals.push({
      start,
      end,
      color: sorted[index].color ?? fallbackColor,
    });
  }

  return intervals;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);
